import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import ChatFeedbackService from './ChatFeedbackService';
import { CaseReader, PageChip, SevChip, StatusChip, RoleChip, fmtDue, fmtDateTime, isOverdue } from './chatFeedbackShared';

/**
 * Feedback แชทของฉัน (admin side)
 *
 * List: my cases in four tabs. Open one: the case as I should read it, then
 * either รับทราบ (answer + what I will say next time) or ขอชี้แจง (reason),
 * and the private thread with the supervisor. The seconds between opening
 * the case and pressing รับทราบ are sent along as readSeconds.
 *
 * Rendered as a page (/chat-feedback) and inside the KeyData drawer
 * (embedded, onChanged tells the banner to refetch).
 */

const TABS = [
  { key: 'pending', label: 'ต้องทำ' },
  { key: 'answered', label: 'รอหัวหน้า' },
  { key: 'discussing', label: 'คุยต่อ' },
  { key: 'closed', label: 'ปิดแล้ว' },
];

const errMsg = (err, fallback) => err.response?.data?.message || fallback;

/* ---------- one open case ---------- */

function CaseDetail({ recipientId, chatconeName, onBack, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [answer, setAnswer] = useState('');
  const [answerNext, setAnswerNext] = useState('');
  const [disputing, setDisputing] = useState(false);
  const [disputeMsg, setDisputeMsg] = useState('');
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const openedAt = useRef(Date.now());

  const load = useCallback(async () => {
    try {
      const res = await ChatFeedbackService.getRecipient(recipientId);
      setDetail(res.data);
    } catch (err) {
      toast.error(errMsg(err, 'โหลดเคสไม่สำเร็จ'));
      onBack();
    }
  }, [recipientId, onBack]);

  useEffect(() => { openedAt.current = Date.now(); load(); }, [load]);

  if (!detail) return <div className="p-6 text-center text-gray-400">กำลังโหลด...</div>;

  const isFix = detail.role === 'fix';
  // Both boxes only have to be non-empty: no minimum length, no banned wording.
  const canAck = !!answer.trim() && (!isFix || !!answerNext.trim());
  const readSeconds = () => Math.round((Date.now() - openedAt.current) / 1000);

  const act = async (fn, okText) => {
    setBusy(true);
    try {
      await fn();
      toast.success(okText);
      onChanged?.();
      await load();
    } catch (err) {
      toast.error(errMsg(err, 'ไม่สำเร็จ'));
    } finally {
      setBusy(false);
    }
  };

  const handleAck = () => act(() => ChatFeedbackService.acknowledge(recipientId, { answer, answerNext: isFix ? answerNext : undefined, readSeconds: readSeconds() }), 'รับทราบแล้ว');
  const handleDispute = () => act(() => ChatFeedbackService.dispute(recipientId, disputeMsg), 'ส่งคำชี้แจงแล้ว');
  const handleReply = () => act(() => ChatFeedbackService.createComment(recipientId, reply), 'ส่งข้อความแล้ว').then(() => setReply(''));

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <button type="button" onClick={onBack} className="text-sm text-indigo-600 hover:text-indigo-800">← กลับ</button>
        <StatusChip status={detail.status} />
        <RoleChip role={detail.role} />
      </div>

      <CaseReader caseData={{ ...detail.case }} recipient={detail} highlightNames={chatconeName ? [chatconeName] : []} />

      {detail.status === 'pending' && (
        <div className="rounded-lg border-2 border-indigo-300 bg-white p-4 space-y-3">
          <div className="font-semibold text-gray-800">รับทราบ</div>
          <div>
            <label className="block text-xs font-semibold text-gray-600 mb-1">{isFix ? 'ทำไมเรื่องนี้ถึงเป็นปัญหา' : 'ถ้าเป็นเรา จะตอบลูกค้าว่าอะไร'}</label>
            <textarea value={answer} onChange={(e) => setAnswer(e.target.value)} rows={3} className="w-full border rounded px-2 py-1 text-sm" />
          </div>
          {isFix && (
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1">ครั้งหน้าจะพิมพ์ตอบลูกค้าว่า</label>
              <textarea value={answerNext} onChange={(e) => setAnswerNext(e.target.value)} rows={2} className="w-full border rounded px-2 py-1 text-sm" />
            </div>
          )}
          <div className="flex items-center gap-3 flex-wrap">
            <button type="button" onClick={handleAck} disabled={!canAck || busy} className="bg-indigo-600 text-white rounded-md px-4 py-2 text-sm font-semibold hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed">รับทราบ</button>
            <button type="button" onClick={() => setDisputing(!disputing)} className="text-sm text-gray-600 hover:text-gray-800 underline">ขอชี้แจง</button>
          </div>
          {disputing && (
            <div className="border-t pt-3 space-y-2">
              <label className="block text-xs font-semibold text-gray-600">เหตุผลที่ขอชี้แจง</label>
              <textarea value={disputeMsg} onChange={(e) => setDisputeMsg(e.target.value)} rows={2} className="w-full border rounded px-2 py-1 text-sm" />
              <button type="button" onClick={handleDispute} disabled={!disputeMsg.trim() || busy} className="border border-gray-400 rounded-md px-4 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">ส่งคำชี้แจง</button>
            </div>
          )}
        </div>
      )}

      {detail.answer && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm space-y-2">
          <div className="text-xs text-gray-500">คำตอบของคุณ · {fmtDateTime(detail.ackAt)}</div>
          <div className="whitespace-pre-wrap">{detail.answer}</div>
          {detail.answerNext && <div className="pt-2 border-t"><span className="text-xs font-semibold text-gray-600">ครั้งหน้า: </span>{detail.answerNext}</div>}
          {detail.verdict && <div className="pt-2 border-t text-xs text-gray-600">ผลจากหัวหน้า: {{ understood: 'เข้าใจแล้ว ปิดเคส', withdrawn: 'ถอนเคส', upheld: 'ยืนยันเคส ต้องรับทราบใหม่' }[detail.verdict]}</div>}
        </div>
      )}

      {(detail.comments.length > 0 || detail.status !== 'pending') && (
        <div className="rounded-lg border border-gray-200 bg-white p-4 space-y-3">
          <div className="font-semibold text-gray-800 text-sm">คุยกับหัวหน้า</div>
          {detail.comments.length === 0 && <div className="text-xs text-gray-400">ยังไม่มีข้อความ</div>}
          {detail.comments.map((c) => (
            <div key={c.id} className="text-sm">
              <div className="text-xs text-gray-500">{c.author?.nickname || '—'} · {fmtDateTime(c.createdAt)}</div>
              <div className="whitespace-pre-wrap">{c.content}</div>
            </div>
          ))}
          {!['closed', 'withdrawn'].includes(detail.status) && (
            <div className="flex gap-2 pt-2 border-t">
              <input value={reply} onChange={(e) => setReply(e.target.value)} placeholder="พิมพ์ข้อความ..." className="flex-1 border rounded px-2 py-1 text-sm" />
              <button type="button" onClick={handleReply} disabled={!reply.trim() || busy} className="border border-gray-400 rounded-md px-3 py-1 text-sm hover:bg-gray-50 disabled:opacity-50">ส่ง</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ---------- the list ---------- */

function ChatFeedbackInbox({ embedded = false, onChanged }) {
  const [me, setMe] = useState(null);
  const [items, setItems] = useState([]);
  const [remember, setRemember] = useState([]);
  const [tab, setTab] = useState('pending');
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [meRes, inboxRes] = await Promise.all([ChatFeedbackService.getMe(), ChatFeedbackService.getInbox()]);
      setMe(meRes.data);
      setItems(inboxRes.data.items);
      setRemember(inboxRes.data.remember);
    } catch (err) {
      toast.error(errMsg(err, 'โหลดไม่สำเร็จ'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleBack = useCallback(() => { setSelectedId(null); load(); }, [load]);

  const counts = TABS.reduce((acc, t) => ({ ...acc, [t.key]: items.filter((i) => i.status === t.key).length }), {});
  const shown = items.filter((i) => i.status === tab);

  return (
    <div className={embedded ? 'p-4 space-y-4' : 'p-4 sm:p-6 max-w-3xl mx-auto space-y-4'}>
      {!embedded && <h1 className="text-2xl font-bold text-gray-800">Feedback แชทของฉัน</h1>}

      {selectedId ? (
        <CaseDetail recipientId={selectedId} chatconeName={me?.chatconeName} onBack={handleBack} onChanged={onChanged} />
      ) : (
        <>
          <div className="flex gap-1 flex-wrap">
            {TABS.map((t) => (
              <button key={t.key} type="button" onClick={() => setTab(t.key)}
                className={`px-3 py-1.5 rounded-full text-sm border ${tab === t.key ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-gray-600 border-gray-300'}`}>
                {t.label}{counts[t.key] ? ` ${counts[t.key]}` : ''}
              </button>
            ))}
          </div>

          {loading && <div className="text-center text-gray-400 py-6">กำลังโหลด...</div>}
          {!loading && shown.length === 0 && <div className="text-center text-gray-400 py-6">ไม่มีเคสในแท็บนี้</div>}

          <div className="space-y-2">
            {shown.map((r) => (
              <button key={r.id} type="button" onClick={() => setSelectedId(r.id)}
                className={`w-full text-left bg-white rounded-xl border px-4 py-3 hover:shadow-sm ${r.overdue ? 'border-red-400' : 'border-gray-300'}`}>
                <div className="text-[15px] font-semibold text-gray-800">{r.case.title || r.case.category}</div>
                <div className="mt-1 flex items-center gap-2 flex-wrap text-xs text-gray-500">
                  <PageChip page={r.case.page} /><SevChip sev={r.case.severity} /><RoleChip role={r.role} />
                  <span>{r.case.reportDate}</span>
                  {r.case.timeRange && <span>· {r.case.timeRange}</span>}
                  {r.status === 'pending' && r.dueAt && (
                    <span className={`ml-auto font-semibold ${isOverdue(r) ? 'text-red-600' : 'text-gray-600'}`}>{isOverdue(r) ? 'เลยกำหนด · ' : ''}ก่อน {fmtDue(r.dueAt)}</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          {tab === 'closed' && remember.length > 0 && (
            <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm">
              <div className="font-semibold text-green-800 mb-2">สิ่งที่ต้องจำ</div>
              <ul className="list-disc pl-5 space-y-1 text-gray-800">
                {remember.map((m) => <li key={m.caseId}><span className="text-gray-500">{m.reportDate} · {m.title}: </span>{m.next}</li>)}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default ChatFeedbackInbox;
